/**
 * PATH: packages/tenant-shared/src/preferences/quotechViewPreferenceAdapter.ts
 * FILE_ID: TENANTSHARED-VIEWPREF-ADAPTER-001
 * PURPOSE: Bind the generic preference store to Quotech auth, storage, and HTTP.
 * ROLE IN ARCHITECTURE: Tenant-aware infrastructure adapter over @quotech/api.
 * MAIN EXPORTS: getQuotechViewPreferenceStore, resolveViewPreferenceIdentity.
 * NON-RESPONSIBILITIES: Does not define domain schemas or React lifecycle.
 * NOTES FOR FUTURE AI: Keep cache and registry keys tenant+user+scope complete.
 *   Cosmetic stores default to last-write-wins retry-local on 412.
 */

import {
  VIEW_PREFERENCE_ENDPOINT,
  VIEW_PREFERENCE_SCHEMA_HEADER,
  createViewPreferenceStore,
  viewPreferenceCacheKey,
} from '@quotech/api/view-preferences'
import type {
  CreateViewPreferenceStoreOptions,
  ViewPreferenceCache,
  ViewPreferenceIdentity,
  ViewPreferenceStore,
  ViewPreferenceTelemetryEvent,
  ViewPreferenceTransport,
} from '@quotech/api/view-preferences'

import { getRuntimeQuotechOrgId, quotechJson } from '../services/quotechApi'
import { TENANT_NAMESPACE, tlsKey } from '../utils/tenantStorage'
import {
  rethrowViewPreferenceMutationError,
  toViewPreferenceEnvelope,
  type ViewPreferenceResponse,
} from './quotechViewPreferenceWire'

const ANONYMOUS_SESSION_KEY = 'quotech_viewpref_anonymous_session'
const TELEMETRY_EVENT = 'quotech:view-preference-telemetry'
const CHANNEL_NAME = 'quotech:view-preferences'
const stores = new Map<string, ViewPreferenceStore<object>>()
let memoryAnonymousId: string | null = null

function randomId(prefix: string): string {
  const uuid =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
  return `${prefix}-${uuid}`
}

function memoryUserId(): string {
  memoryAnonymousId ??= randomId('anonymous-memory')
  return memoryAnonymousId
}

function sessionUserId(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  try {
    const pinned = sessionStorage.getItem('quotech_tab_user_id')?.trim()
    if (pinned) return pinned
    const existing = sessionStorage.getItem(ANONYMOUS_SESSION_KEY)?.trim()
    if (existing) return existing
    const created = randomId('anonymous-session')
    sessionStorage.setItem(ANONYMOUS_SESSION_KEY, created)
    return created
  } catch {
    return memoryUserId()
  }
}

/**
 * Resolve a non-shared tenant/user identity for preference cache boundaries.
 *
 * Inputs: optional authenticated user id supplied by the host.
 * Output: tenant plus explicit, pinned, or tab-ephemeral user segment.
 * Side effects: may create one anonymous session id in sessionStorage.
 * Failure modes: blocked storage falls back to an in-memory random id.
 */
export function resolveViewPreferenceIdentity(
  userId?: string | number | null,
): ViewPreferenceIdentity {
  const explicit = userId === null || userId === undefined ? '' : String(userId).trim()
  return {
    tenant: TENANT_NAMESPACE,
    userId: explicit || sessionUserId() || memoryUserId(),
  }
}

function storageKey(key: string): string {
  return tlsKey(key)
}

function readStorage(key: string): unknown {
  if (typeof localStorage === 'undefined') return undefined
  try {
    const raw = localStorage.getItem(storageKey(key))
    return raw === null ? undefined : JSON.parse(raw)
  } catch {
    return undefined
  }
}

function channel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null
  try {
    return new BroadcastChannel(CHANNEL_NAME)
  } catch {
    return null
  }
}

const syncChannel = channel()

/** Browser cache with native storage and BroadcastChannel cross-tab delivery. */
export const quotechViewPreferenceCache: ViewPreferenceCache = {
  read: readStorage,
  write(key, value) {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.setItem(storageKey(key), JSON.stringify(value))
      syncChannel?.postMessage({ key, value })
    } catch {
      // Cosmetic preferences stay usable in memory when storage is unavailable.
    }
  },
  remove(key) {
    if (typeof localStorage === 'undefined') return
    try {
      localStorage.removeItem(storageKey(key))
      syncChannel?.postMessage({ key, value: undefined })
    } catch {
      // Fail soft: a reset still updates the mounted store.
    }
  },
  subscribe(key, listener) {
    if (typeof window === 'undefined') return () => {}
    const onStorage = (event: StorageEvent) => {
      if (event.key !== storageKey(key)) return
      try {
        listener(event.newValue === null ? undefined : JSON.parse(event.newValue))
      } catch {
        listener(undefined)
      }
    }
    const onChannel = (event: MessageEvent<{ key?: string; value?: unknown }>) => {
      if (event.data?.key === key) listener(event.data.value)
    }
    window.addEventListener('storage', onStorage)
    syncChannel?.addEventListener('message', onChannel)
    return () => {
      window.removeEventListener('storage', onStorage)
      syncChannel?.removeEventListener('message', onChannel)
    }
  },
}

const SHELL_ORG_STORAGE_KEYS = ['CurrentTenantId', 'org_id'] as const

function positiveOrgId(raw: string | null | undefined): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  return /^[1-9]\d*$/.test(trimmed) ? trimmed : null
}

function orgIdFromCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|; )quotech_org=([^;]*)/)
  if (!match) return null
  try {
    const parsed = JSON.parse(decodeURIComponent(match[1])) as { id?: unknown }
    return positiveOrgId(parsed?.id == null ? null : String(parsed.id))
  } catch {
    return null
  }
}

/**
 * The organisation the rest of the shell already sends as X-Organisation-ID.
 *
 * A view-preference call with no org signal 409s for a multi-org user
 * (ActiveOrganisationRequired). A second ?tenant= that disagrees with the
 * header 409s as a context conflict. One header, no query alias.
 */
function shellOrganisationId(): string | null {
  if (typeof localStorage !== 'undefined') {
    for (const key of SHELL_ORG_STORAGE_KEYS) {
      const stored = positiveOrgId(localStorage.getItem(key))
      if (stored) return stored
    }
  }
  return orgIdFromCookie() ?? positiveOrgId(
    getRuntimeQuotechOrgId() == null ? null : String(getRuntimeQuotechOrgId()),
  )
}

function preferenceHeaders(extra: Record<string, string>): Record<string, string> {
  const orgId = shellOrganisationId()
  if (!orgId) return extra
  return { ...extra, 'X-Organisation-ID': orgId }
}

/** Concrete transport preserving full-blob ETag/schema/correlation metadata. */
export const quotechViewPreferenceTransport: ViewPreferenceTransport = {
  async get({ scopeKey, actionId }) {
    const { data, response } = await quotechJson<ViewPreferenceResponse>(
      `${VIEW_PREFERENCE_ENDPOINT}?scope_key=${encodeURIComponent(scopeKey)}`,
      {
        method: 'GET',
        includeTenant: false,
        skipIdentityAssert: true,
        headers: preferenceHeaders({ 'X-Correlation-ID': actionId }),
      },
    )
    return toViewPreferenceEnvelope(data, response)
  },
  async patch({ scopeKey, state, ifMatch, schemaVersion, actionId }) {
    try {
      const { data, response } = await quotechJson<ViewPreferenceResponse>(
        VIEW_PREFERENCE_ENDPOINT,
        {
          method: 'PATCH',
          includeTenant: false,
          skipIdentityAssert: true,
          headers: preferenceHeaders({
            'If-Match': ifMatch,
            [VIEW_PREFERENCE_SCHEMA_HEADER]: String(schemaVersion),
            'X-Correlation-ID': actionId,
          }),
          body: { scope_key: scopeKey, state },
        },
      )
      return toViewPreferenceEnvelope(data, response)
    } catch (error) {
      rethrowViewPreferenceMutationError(error)
    }
  },
  async delete({ scopeKey, ifMatch, schemaVersion, actionId }) {
    try {
      const { data, response } = await quotechJson<ViewPreferenceResponse>(
        `${VIEW_PREFERENCE_ENDPOINT}?scope_key=${encodeURIComponent(scopeKey)}`,
        {
          method: 'DELETE',
          includeTenant: false,
          skipIdentityAssert: true,
          headers: preferenceHeaders({
            'If-Match': ifMatch,
            [VIEW_PREFERENCE_SCHEMA_HEADER]: String(schemaVersion),
            'X-Correlation-ID': actionId,
          }),
        },
      )
      return toViewPreferenceEnvelope(data, response)
    } catch (error) {
      rethrowViewPreferenceMutationError(error)
    }
  },
}

function currentActionId(): string {
  const active =
    typeof window === 'undefined'
      ? null
      : (window as { __QT_OBS__?: { currentActionId?: string | null } })
          .__QT_OBS__?.currentActionId
  return active || randomId('viewpref')
}

function reportTelemetry(event: ViewPreferenceTelemetryEvent): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent(TELEMETRY_EVENT, { detail: event }))
}

export type QuotechViewPreferenceStoreOptions<T extends object> = Omit<
  CreateViewPreferenceStoreOptions<T>,
  'cache' | 'transport' | 'createActionId' | 'telemetry'
>

/**
 * Return one shared store for an exact tenant, user, and scope key.
 *
 * Inputs: identity, scope, domain defaults/codec, schema and debounce policy.
 * Output: stable store reused by every compatible hook/provider consumer.
 * Side effects: lazily registers cache listeners; network starts on hydrate.
 * Failure modes: invalid identity/scope throws through the generic key builder.
 */
export function getQuotechViewPreferenceStore<T extends object>(
  options: QuotechViewPreferenceStoreOptions<T>,
): ViewPreferenceStore<T> {
  const key = viewPreferenceCacheKey(options.identity, options.scopeKey)
  const existing = stores.get(key)
  if (existing) return existing as ViewPreferenceStore<T>
  const store = createViewPreferenceStore({
    conflictPolicy: 'retry-local',
    ...options,
    cache: quotechViewPreferenceCache,
    transport: quotechViewPreferenceTransport,
    createActionId: currentActionId,
    telemetry: reportTelemetry,
  })
  stores.set(key, store as ViewPreferenceStore<object>)
  return store
}

/**
 * Flush every registered store. Used by app adapters that previously
 * drained their own debounce maps on pagehide.
 */
export async function flushAllQuotechViewPreferenceStores(): Promise<void> {
  await Promise.all([...stores.values()].map((store) => store.flush()))
}
